/* One Jam song: choose the backing, tempo and input, play it on the stage, then see the results.
   The speakers never cover the guitar: without headphones only the count-in (or drums, which do not
   pause the microphone) plays, and the full band needs headphones. */
import { useMemo, useState } from 'react';
import { useGo } from '../../app/navigation';
import type { PreparedSong } from '../../content/songs';
import { audio } from '../../core/audio';
import { BASE_POINTS, buildChart, type JamChart, type JamMark, type JamResult, maxScore } from '../../core/jam';
import { useMicState } from '../../core/mic';
import { addXp, award, setSetting, useSettings } from '../../core/store';
import { pretty } from '../../core/theory';
import { fmtTime, scrollTop } from '../../core/util';
import { Btn } from '../../ui/Button';
import { ChordDiagram } from '../../ui/ChordDiagram';
import { Icon } from '../../ui/icons';
import { Rich } from '../../ui/Rich';
import { modStyle } from '../../ui/style';
import { Stars } from '../../ui/widgets';
import { MicBlock } from '../exercise/MicBlock';
import { LEVEL_NAME } from '../songs/SongList';
import { type Backing, type JamInput, type JamOptions, JamRun } from './JamStage';
import { type JamRecord, loadJamRecord, saveJamRecord } from './records';

const TEMPOS = [0.7, 0.85, 1] as const;
const fmt = (n: number) => Math.round(n).toLocaleString('tr-TR');

function Choice<T extends string | number>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { id: T; text: string; disabled?: boolean }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        // biome-ignore lint/a11y/useSemanticElements: segmented control keeps the shared button markup
        <button
          key={String(o.id)}
          type="button"
          role="radio"
          aria-checked={value === o.id}
          className={value === o.id ? 'on' : undefined}
          disabled={o.disabled}
          onClick={() => onChange(o.id)}
        >
          {o.text}
        </button>
      ))}
    </div>
  );
}

interface Finished {
  r: JamResult;
  opts: JamOptions;
  xp: number;
  prev: JamRecord | null;
  newBest: boolean;
}

export function JamGame({ song }: { song: PreparedSong }) {
  const go = useGo();
  const mic = useMicState();
  const { headphones } = useSettings();
  const [backing, setBacking] = useState<Backing | null>(null);
  const [tempo, setTempo] = useState<number>(1);
  const [inputChoice, setInputChoice] = useState<JamInput | null>(null);
  const [run, setRun] = useState<{ opts: JamOptions; chart: JamChart; n: number } | null>(null);
  const [finished, setFinished] = useState<Finished | null>(null);
  const preview = useMemo(() => buildChart(song), [song]);
  const record = loadJamRecord(song.id);

  // the full band only with headphones; without them the count-in is the default
  const chosen: Backing = backing ?? (headphones ? 'full' : 'count');
  const eff: Backing = chosen === 'full' && !headphones ? 'count' : chosen;
  const input: JamInput = mic === 'on' ? (inputChoice ?? 'mic') : 'screen';
  const base = Math.round(BASE_POINTS * tempo);

  const start = async () => {
    await audio.resume();
    audio.stopAll();
    const opts: JamOptions = { backing: eff, tempo, input };
    setFinished(null);
    setRun({ opts, chart: buildChart(song, tempo), n: Date.now() });
  };

  const onDone = (r: JamResult) => {
    const opts = run?.opts ?? { backing: eff, tempo, input };
    const guitar = opts.input === 'mic';
    const saved = guitar && !r.aborted ? saveJamRecord(song.id, r) : null;
    const xp = Math.round(r.hits / 2) + r.stars * 5;
    if (xp > 0) addXp(xp, 'akor');
    if (guitar && !r.aborted && r.acc >= 0.7) award('birlikte');
    setFinished({ r, opts, xp, prev: saved ? saved.prev : record, newBest: !!saved?.newBest });
    setRun(null);
    scrollTop();
  };

  const backingNote =
    eff === 'full'
      ? 'Davul, bas ve gitar eşliği kulaklığından çalar; mikrofon yalnızca seni duyar.'
      : eff === 'drums'
        ? headphones
          ? 'Yalnızca davul çalar.'
          : 'Davul hoparlörden çalar. Davul sesi mikrofonu durdurmaz ama kulaklıkla algılama daha temiz olur.'
        : 'Bir ölçü sayım duyarsın, sonra yalnızca sen çalarsın. Vuruşları çizgideki ışık gösterir.';

  return (
    <div className="jam-page" style={modStyle('akor')}>
      <button type="button" className="link-back" onClick={() => go('jam')}>
        <Icon name="left" />
        <span>Jam</span>
      </button>
      {run ? (
        <>
          <div className="jam-title">
            <h1>{song.title}</h1>
            <span>{song.cover ? song.cover.artist : 'kulaktan.com'}</span>
            <span className={`sg-tag lv${song.level}`}>{LEVEL_NAME[song.level]}</span>
          </div>
          <JamRun key={run.n} song={song} chart={run.chart} opts={run.opts} onDone={onDone} />
        </>
      ) : finished ? (
        <JamResults
          song={song}
          f={finished}
          onRetry={start}
          onSetup={() => {
            setFinished(null);
            scrollTop();
          }}
        />
      ) : (
        <div className="jam-start">
          <div className="ex-start-card jam-intro">
            <div className="sg-meta">
              <span className={`sg-tag lv${song.level}`}>{LEVEL_NAME[song.level]}</span>
              <span className="sg-tag">{song.cover ? song.cover.artist : 'kulaktan.com'}</span>
              <span className="sg-tag">{song.style}</span>
              <span className="sg-tag">
                {song.beats}/4 · {song.bpm} BPM
              </span>
              <span className="sg-tag">{fmtTime(preview.length)}</span>
            </div>
            <h1 className="ex-start-title">{song.title}</h1>
            <p className="ex-start-desc">
              <Rich text={song.desc} />
            </p>
            <ul className="ex-rules">
              <li>Akorlar yukarıdan vuruş çizgisine doğru akar. Akor çizgiye gelince çal.</li>
              <li>Her doğru akor {base} puan. Art arda 4 doğruda çarpan ×2, 8 doğruda ×4, 12 doğruda ×8 olur.</li>
              <li>Yanlış akor çalarsan ya da bir ölçüyü kaçırırsan çarpan ×1'e, seri sıfıra döner.</li>
              <li>
                {song.cover
                  ? 'Bu şarkının sözleri telifli olduğu için burada yalnızca akorları var.'
                  : 'Sözler, söylenecekleri anda alttaki satırda yanar.'}
              </li>
            </ul>
            <div className="jam-chords">
              {preview.lanes.map((c) => (
                <ChordDiagram key={c} chord={c} />
              ))}
            </div>
            {record ? (
              <div className="ex-best">
                <Stars n={record.stars} />
                <span>
                  En iyi skorun: {fmt(record.best)} · İsabet %{Math.round(record.acc * 100)}
                </span>
              </div>
            ) : null}
          </div>
          <div className="panel jam-setup">
            <div className="jam-opt">
              <span className="jo-k">Eşlik</span>
              <Choice<Backing>
                label="Eşlik"
                value={eff}
                onChange={setBacking}
                options={[
                  { id: 'count', text: 'Yalnızca sayım' },
                  { id: 'drums', text: 'Davul' },
                  { id: 'full', text: 'Tam eşlik', disabled: !headphones },
                ]}
              />
              <p className="mb-note">{backingNote}</p>
            </div>
            <div className={`jam-phones${headphones ? ' on' : ''}`}>
              <Icon name="headphones" />
              {headphones ? (
                <p>
                  Kulaklık takılı: eşlik mikrofona karışmaz.{' '}
                  <button type="button" className="link" onClick={() => setSetting('headphones', false)}>
                    Kulaklığı çıkardım
                  </button>
                </p>
              ) : (
                <p>
                  Hoparlörden çalan eşlik mikrofona karışır, bu yüzden tam eşlik yalnızca kulaklıkla açılır.{' '}
                  <button type="button" className="link" onClick={() => setSetting('headphones', true)}>
                    Kulaklık taktım
                  </button>
                </p>
              )}
            </div>
            <div className="jam-opt">
              <span className="jo-k">Tempo</span>
              <Choice<number>
                label="Tempo"
                value={tempo}
                onChange={setTempo}
                options={TEMPOS.map((x) => ({ id: x, text: x === 1 ? 'Asıl tempo' : `%${Math.round(x * 100)}` }))}
              />
              <p className="mb-note">
                {Math.round(song.bpm * tempo)} BPM
                {tempo < 1 ? ` · yavaş tempoda doğru akor başına ${base} puan alırsın.` : ''}
              </p>
            </div>
            {mic === 'on' ? (
              <div className="jam-opt">
                <span className="jo-k">Nasıl çalacaksın?</span>
                <Choice<JamInput>
                  label="Giriş"
                  value={input}
                  onChange={setInputChoice}
                  options={[
                    { id: 'mic', text: 'Gitarla' },
                    { id: 'screen', text: 'Ekrandan' },
                  ]}
                />
                {input === 'screen' ? (
                  <p className="mb-note">Ekrandan çalınca skorun rekor olarak kaydedilmez.</p>
                ) : null}
              </div>
            ) : (
              <MicBlock
                text="Gitarını dinleyebilmem için mikrofonu aç. Açmazsan akorları ekrandan dokunarak çalabilirsin."
                screenNote="ya da ekrandan çal"
              />
            )}
            <div className="ex-start-actions">
              <Btn kind="primary" size="lg" icon="play" onClick={start}>
                {input === 'mic' ? 'Başla' : 'Ekrandan başla'}
              </Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MARK_CLASS: Record<JamMark, string> = { hit: 'hit', wrong: 'wrong', miss: 'miss' };

function JamResults({
  song,
  f,
  onRetry,
  onSetup,
}: {
  song: PreparedSong;
  f: Finished;
  onRetry: () => void;
  onSetup: () => void;
}) {
  const go = useGo();
  const { r, opts } = f;
  const guitar = opts.input === 'mic';
  const title = r.aborted
    ? 'Yarıda bıraktın'
    : r.stars === 3
      ? 'Sahne senin!'
      : r.stars === 2
        ? 'Çok iyi çaldın'
        : r.stars === 1
          ? 'Güzel başlangıç'
          : 'Biraz daha pratik';
  const best = Math.round(maxScore(r.total, Math.round(BASE_POINTS * opts.tempo)));
  let gi = 0;
  return (
    <div className="results jam-results" data-score={r.score}>
      <div className="rs-top">
        <Stars n={r.stars} />
        <h1>{title}</h1>
        {f.newBest ? <div className="rs-badge">Yeni rekor!</div> : null}
        <p className="rs-note">
          {r.aborted
            ? 'Şarkıyı bitirmeden durdurdun; bu oyun rekor olarak kaydedilmedi.'
            : !guitar
              ? 'Ekrandan çaldın: skorun rekor olarak kaydedilmedi. Gitarınla çalınca kaydedilir.'
              : r.stars >= 2
                ? 'Tempoyu yükseltmeyi ya da daha zor bir şarkıyı dene.'
                : 'Kırmızı ölçülerdeki geçişleri yavaş tempoda çalış, sonra yeniden dene.'}
        </p>
      </div>
      <div className="rs-stats">
        <div className="rs-stat">
          <span className="rs-k">Puan</span>
          <span className="rs-v jr-score">{fmt(r.score)}</span>
          <span className="rs-k">en fazla {fmt(best)}</span>
        </div>
        <div className="rs-stat">
          <span className="rs-k">İsabet</span>
          <span className="rs-v jr-acc">%{Math.round(r.acc * 100)}</span>
          <span className="rs-k">
            {r.hits}/{r.aborted ? r.judged : r.total} ölçü
          </span>
        </div>
        <div className="rs-stat">
          <span className="rs-k">En uzun seri</span>
          <span className="rs-v">{r.bestStreak}</span>
          <span className="rs-k">en yüksek çarpan ×{r.maxMult}</span>
        </div>
        <div className="rs-stat">
          <span className="rs-k">Kazanılan XP</span>
          <span className="rs-v">+{f.xp}</span>
          {f.prev ? <span className="rs-k">önceki en iyi {fmt(f.prev.best)}</span> : null}
        </div>
      </div>
      <div className="chart jam-review">
        {song.sections.map((sec, si) => (
          // biome-ignore lint/suspicious/noArrayIndexKey: sections are positional and names may repeat
          <div className="chart-sec" key={si}>
            <div className="cs-head">
              <span className="cs-name">{sec.name}</span>
            </div>
            <div className="bars">
              {sec.bars.map((b) => {
                const i = gi++;
                const m = r.marks[i];
                return (
                  <div key={i} className={`bar${m ? ` ${MARK_CLASS[m]}` : ''}`} data-bar={i}>
                    <span className="b-n">{i + 1}</span>
                    <span className="b-chord">{pretty(b)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
      <p className="mb-note jam-legend">
        <span className="jl-dot hit" /> doğru <span className="jl-dot wrong" /> yanlış akor{' '}
        <span className="jl-dot miss" /> kaçan ölçü
      </p>
      <div className="rs-actions">
        <Btn kind="primary" icon="replay" onClick={onRetry}>
          Tekrar çal
        </Btn>
        <Btn kind="ghost" icon="sliders" onClick={onSetup}>
          Ayarları değiştir
        </Btn>
        <Btn kind="ghost" icon="chordbox" onClick={() => go(`akor.sarki.${song.id}`)}>
          Akorları çalış
        </Btn>
        <Btn kind="ghost" onClick={() => go('jam')}>
          Başka şarkı
        </Btn>
      </div>
    </div>
  );
}
